import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import moment from "moment";
import { shareToast } from "./ShareToast";
import {
  FacebookShareButton,
  LinkedinShareButton,
} from "react-share";

import FeedGallery from "./FeedGallery";
import ShareSheet from "./ShareSheet";
import { TOKENS, FONT, NUM_FEATURES } from "./feedTokens";
import socket from "../../configs/socket";
import socketConstants from "../../constants/socket";
import { formatCurrency } from "../../helpers/formatter";
import { GET_IMAGE } from "../../helpers/images";
import SlowClap from "../../components/SlowClap/SlowClap";
import { ADD_REACT, DELETE_REACT } from "../../api/react";
import { REACTION_PROPERTY_TYPE } from "../../constants/reactions";
import { addComment, getJob } from "../../redux/jobs/actions";
import CommentList from "../../components/Comments/CommentList";
import { ADD_COMMENT, UPDATE_COMMENT, DELETE_COMMENT } from "../../api/comments";
import { UseIsAdmin, UseUserDetails } from "../../hooks/auth";
import { UseCompanyKpis } from "../../hooks/kpi";
import { UseCompanyLogo, UseCompanyColors, UseYetiStats } from "../../hooks/company";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import {
  getFacebookShareable,
  getSocialUrl,
} from "../../components/Feed/socialImage";
import {
  DEFAULT_FEED_SETTINGS,
  shouldShowFeedKpiEntry,
} from "../../helpers/feedKpiSettings";
import { resolveJobRoleTag } from "../../helpers/timelineEntry";
import { recomputeEntryPts, indexKpisById } from "../../helpers/kpiPoints";

const { SOCKET_EVENT } = socketConstants;

/**
 * Extract just the city from a US address string.
 * Handles both formats:
 *   - With commas: "1635 NW Eagle Bluff Ln, Bremerton, WA 98311" → "Bremerton"
 *   - Without commas: "1635 NW Eagle Bluff Ln Bremerton WA 98311" → "Bremerton"
 *   - Multi-word cities: "123 Main St San Francisco CA 94105" → "San Francisco"
 * Returns null if it can't confidently extract a city.
 */
const STREET_SUFFIXES = new Set([
  "st", "ave", "ln", "rd", "dr", "blvd", "way", "ct", "pl", "hwy", "pkwy",
  "cir", "ter", "trl", "sq", "loop", "row",
  "street", "avenue", "lane", "road", "drive", "boulevard", "court", "place",
  "highway", "parkway", "circle", "terrace", "trail", "square",
]);
function extractCity(address) {
  if (!address || typeof address !== "string") return null;
  // Comma-separated path is the easy + reliable case.
  const parts = address.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) return parts[parts.length - 2];
  // Space-separated fallback: scan from the end for state code, then street suffix.
  const tokens = address.trim().split(/\s+/);
  if (tokens.length < 3) return null;
  let stateIdx = -1;
  for (let i = tokens.length - 2; i >= 0; i -= 1) {
    if (/^[A-Z]{2}$/.test(tokens[i])) { stateIdx = i; break; }
  }
  if (stateIdx <= 0) return null;
  let suffixIdx = -1;
  for (let i = stateIdx - 1; i >= 0; i -= 1) {
    const w = tokens[i].toLowerCase().replace(/[.,]/g, "");
    if (STREET_SUFFIXES.has(w)) { suffixIdx = i; break; }
  }
  // City = tokens between street suffix and state code; fallback to single token before state.
  if (suffixIdx === -1) return tokens[stateIdx - 1] || null;
  return tokens.slice(suffixIdx + 1, stateIdx).join(" ") || null;
}

/* ---------- design tokens ---------- */
/* ---------- helpers ---------- */
// Only Upsell / Revenue Credit / Rev-per-hour ever appear in the job card's KPI
// grid — everything else (Review, Yard Sign, MX Plan, Leads, Calls, any other
// workspace KPI) is dropped outright, unlike the other Feed Settings toggles
// which are a per-user preference.
//
// Companies name these very differently ("Revenue" vs "Revenue Credit" vs "Rev
// Cred/hr"), so this matches loosely on keyword fragments rather than the
// exact category keys the toggle system uses — a false positive here just
// means one extra revenue/upsell-flavored tile shows, which is harmless.
const isAllowedFeedKpiTitle = (title) => {
  const t = (title || "").toLowerCase();
  // "Total revenue" is the whole job's revenue (already shown as "Job total"
  // in the meta strip above) rather than the poster's Revenue Credit, so it's
  // excluded even though it shares the "revenue" keyword.
  if (t.includes("total")) return false;
  const isUpsell = t.includes("upsell") || (t.includes("up") && t.includes("sell"));
  const isRevenue = t.includes("revenue") || t.includes("credit");
  // Must read as a *rate* ("per hr", "per hour", "/hr", "rph") — a bare "hour"
  // match let scorekeeper-only metrics like "Hours worked" sneak into the
  // feed card, which isn't a revenue-per-hour figure at all.
  const isRevPerHour = /per\s*hr\b/.test(t) || /per\s*hour/.test(t) || t.includes("/hr") || /\bhr\b/.test(t) || /\brph\b/.test(t);
  return isUpsell || isRevenue || isRevPerHour;
};

const isMonetaryKpiTitle = (title) =>
  /(revenue|sell|sale|payment|amount|dollar|\$|cash)/i.test(title || "");

const formatKpiValue = (kpi) => {
  const amt = Math.round(kpi?.amount || 0);
  return isMonetaryKpiTitle(kpi.title) ? `$${amt.toLocaleString()}` : amt.toLocaleString();
};

const rolePillStyle = (tag) => {
  if (tag === "solo tech") return { bg: TOKENS.goodSoft, fg: TOKENS.good };
  if (tag === "lead (with assistant)") return { bg: TOKENS.primarySoft, fg: TOKENS.primary };
  if (tag === "two leads") return { bg: TOKENS.purpleSoft, fg: TOKENS.purple };
  if (tag === "training") return { bg: TOKENS.coralSoft, fg: TOKENS.coral };
  // Not Tagged / unknown
  return { bg: TOKENS.surface2, fg: TOKENS.muted };
};

const buildMapsUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

/* ============================================================ */
/* FeedCard                                                     */
/* ============================================================ */
export default function FeedCard({ job, settings, rcAmount, rcPoints, elementId, autoOpenComments }) {
  const dispatch = useDispatch();
  const currentUser = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const companyKpis = UseCompanyKpis();
  const companyLogo = UseCompanyLogo();
  const { primaryColor } = UseCompanyColors();
  const yetiStats = UseYetiStats();

  const { user, amount: jobTotal, metadata } = job;
  const isOwner = job.userId === currentUser?._id || isAdmin;

  /* ---- Default settings if none provided ---- */
  const cfg = settings || DEFAULT_FEED_SETTINGS;

  /* ---- Points (job KPIs + claims + revenue credit bonus) ----
     Recomputed from each entry's amount × (pointValue / pointScale) using the
     job poster's workspace KPI config, so the +pts badge stays in sync with
     the leaderboard / Scorekeeper / KOTM. Anchor metrics keep the server's
     stored pts. Revenue credit is recomputed from rcAmount using the matching
     Revenue Credit KPI in groupKpis, so rcPoints from the server is ignored
     when we have enough config to reproduce it.

     Double-count guard: when we're going to add Revenue Credit from rcAmount,
     skip any Revenue Credit row already inside job.kpis / job.claims. Without
     this, the same RC contribution lands twice — once from the per-row reduce
     (which recomputes the job.kpis row), once from the rcAmount branch — and
     the badge ends up at ~2× the right number. */
  const points = useMemo(() => {
    // Right after login the KPI store hasn't hydrated yet — companyKpis is
    // null for a render, and indexing into it white-screened the whole feed.
    const groupKpis = companyKpis?.[user?.groupId] || [];
    const kpiById = indexKpisById(groupKpis);
    const revenueCreditKpi = groupKpis.find((k) =>
      k?.title?.toLowerCase()?.includes("revenue"),
    );
    const usingRcAmount = rcAmount != null && !!revenueCreditKpi;
    const rcKpiId = revenueCreditKpi?._id;

    // Only the POSTER's own entries count toward their card's badge. The
    // `kpis` virtual returns every EmployeeKpi row referencing the job —
    // including other players' rows and rows from other workspaces' KPIs
    // (e.g. a test "Revenue" KPI at 50 pts/$ once inflated a technician's
    // card from ~304 pts to +177,350). Foreign-workspace rows are doubly
    // wrong: their KPI isn't in kpiById, so the recompute would fall back
    // to the row's stored points at that other KPI's scale.
    const posterId = String(job.userId || job.user?._id || "");
    const jobKpis = [...(job.kpis || []), ...(job.claims || [])].filter(
      (k) => String(k?.ownerId || "") === posterId && kpiById[k?.kpiId],
    );
    let total = jobKpis.reduce((sum, k) => {
      if (usingRcAmount && k?.kpiId === rcKpiId) return sum; // counted via rcAmount
      return sum + recomputeEntryPts(k, kpiById[k.kpiId]);
    }, 0);

    if (usingRcAmount) {
      // Recompute from the amount we display in the breakdown so the +pts
      // badge matches the row beneath it.
      total += recomputeEntryPts({ amount: rcAmount }, revenueCreditKpi);
    } else if (rcPoints) {
      total += rcPoints;
    }

    return Math.floor(total);
  }, [companyKpis, user?.groupId, job.kpis, job.claims, rcAmount, rcPoints]);

  /* ---- KPI summary (workspace KPIs + scores + Yeti delta) ---- */
  const kpiSummary = useMemo(() => {
    const groupKpis = companyKpis?.[user?.groupId] || [];
    if (!groupKpis.length) return [];
    // Same poster-only rule as the badge: a teammate's entry on the same job
    // (e.g. a second lead's split credit) must not surface on this card.
    const posterId = String(job.userId || job.user?._id || "");
    const jobKpis = [...(job.kpis || []), ...(job.claims || [])].filter(
      (k) => String(k?.ownerId || "") === posterId,
    );
    const yetiBucket = yetiStats?.month || {};

    return groupKpis
      .filter((kpi) => kpi.disabled !== true)
      .map((kpi) => {
        const score = jobKpis.find((s) => s.kpiId === kpi._id);
        const playerAmt = score?.amount || 0;
        const yetiAmt = yetiBucket?.[kpi._id]?.amt || 0;
        const yetiDiff = playerAmt - yetiAmt;

        return {
          kpiId: kpi._id,
          title: kpi.title,
          isActive: !!score,
          amount: playerAmt,
          points: score?.points || 0,
          yetiDiff,
          // For revenue credit: prefer the rcAmount injected from FeedPage
          isRevenueCredit: kpi.title?.toLowerCase().includes("revenue"),
        };
      })
      .map((k) => {
        if (k.isRevenueCredit && rcAmount != null) {
          return { ...k, amount: rcAmount };
        }
        return k;
      })
      // Honor Feed Settings: hide a KPI if its per-id toggle is off (falls back
      // to the legacy title-based category for older saved settings).
      .filter((k) => shouldShowFeedKpiEntry({ _id: k.kpiId, title: k.title }, cfg))
      // Only Upsell, Revenue Credit, and Rev/hr are ever shown on the card.
      .filter((k) => isAllowedFeedKpiTitle(k.title))
      // Sort: active KPIs first, then zero
      .sort((a, b) => Number(b.isActive) - Number(a.isActive));
  }, [companyKpis, user?.groupId, job.kpis, job.claims, yetiStats, cfg, rcAmount]);

  /* ---- Photos ---- */
  const images = useMemo(
    () =>
      (job.images || []).map((img) => ({
        src: GET_IMAGE(img.src),
        tag: img.tag,
      })),
    [job.images],
  );
  const [showGallery, setShowGallery] = useState(false);
  const [gallery, setGallery] = useState([]);

  /* ---- Photo carousel ---- */
  const trackRef = useRef(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const onTrackScroll = () => {
    const t = trackRef.current;
    if (!t) return;
    const idx = Math.round(t.scrollLeft / t.clientWidth);
    if (idx !== photoIdx) setPhotoIdx(idx);
  };
  const goToPhoto = (delta) => {
    const t = trackRef.current;
    if (!t) return;
    t.scrollBy({ left: delta * t.clientWidth, behavior: "smooth" });
  };

  /* ---- Reactions / comments ---- */
  const [reacts, setReacts] = useState(job.reacts || []);
  useEffect(() => setReacts(job.reacts || []), [job.reacts]);
  const pendingReactAdds = useRef({});
  const [showComments, setShowComments] = useState(!!autoOpenComments);

  useEffect(() => {
    // Missing cleanup meant a remount for the same job (list re-sorts/merges
    // happen often in a live feed) left the previous listener stacked on the
    // shared socket — each accumulated listener fires its own getJob refetch
    // per broadcast, and the resulting concurrent requests can resolve out of
    // order and flicker the UI between stale/fresh state.
    const handleRefetch = (data) => {
      if (data.jobId === job._id) dispatch(getJob(job._id));
    };
    socket.on(SOCKET_EVENT.NOTIFY_REFETCH_JOB, handleRefetch);
    socket.emit(SOCKET_EVENT.JOIN_JOB, job._id);
    return () => socket.off(SOCKET_EVENT.NOTIFY_REFETCH_JOB, handleRefetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job._id]);

  const onReactToggle = async (propertyId, currentUserId, isActive, react, iconType, propertyModel) => {
    // Optimistic UI: reflect the tap instantly instead of waiting on the
    // ADD_REACT/DELETE_REACT round trip + full job refetch to paint.
    if (isActive) {
      const tempId = `temp_${currentUserId}_${Date.now()}`;
      setReacts((prev) => [...prev, { _id: tempId, users: [{ _id: currentUserId }] }]);
      if (propertyModel === REACTION_PROPERTY_TYPE.JOB) {
        socket.emit(SOCKET_EVENT.POST_SLOW_CLAP, { sender: currentUser._id, jobId: job._id });
      } else if (propertyModel === REACTION_PROPERTY_TYPE.COMMENT) {
        socket.emit(SOCKET_EVENT.POST_COMMENT_LIKE, { sender: currentUser._id, commentId: propertyId, jobId: job._id });
      }
      const addPromise = ADD_REACT({ propertyId, data: { userId: currentUserId, propertyModel, iconType } }).then(
        ({ data: added }) => {
          setReacts((prev) => prev.map((r) => (r._id === tempId ? added : r)));
          return added;
        },
      );
      pendingReactAdds.current[tempId] = addPromise;
      try {
        await addPromise;
        socket.emit(SOCKET_EVENT.REFRESH_JOB, { jobId: job._id });
        dispatch(getJob(job._id));
      } catch (err) {
        setReacts((prev) => prev.filter((r) => r._id !== tempId));
      } finally {
        delete pendingReactAdds.current[tempId];
      }
    } else {
      setReacts((prev) => prev.filter((r) => r._id !== react._id));
      try {
        // If this reaction hasn't finished being created yet (rapid tap-off),
        // wait for the real id instead of deleting the temporary local one.
        const pendingAdd = pendingReactAdds.current[react._id];
        const realId = pendingAdd ? (await pendingAdd)._id : react._id;
        await DELETE_REACT(realId);
        socket.emit(SOCKET_EVENT.REFRESH_JOB, { jobId: job._id });
        dispatch(getJob(job._id));
      } catch (err) {
        setReacts((prev) => [...prev, react]);
      }
    }
  };

  const onAddComment = async (jobId, senderId, content) => {
    const { data: comment } = await ADD_COMMENT({
      jobId,
      data: { userId: senderId, content, propertyModel: "Job" },
    });
    dispatch(addComment({ jobId, comment }));
    socket.emit(SOCKET_EVENT.REFRESH_JOB, { jobId });
    socket.emit(SOCKET_EVENT.POST_COMMENT, { sender: senderId, jobId, content });
  };

  const onEditComment = async (commentId, content) => {
    await UPDATE_COMMENT(commentId, content);
    socket.emit(SOCKET_EVENT.REFRESH_JOB, { jobId: job._id });
    dispatch(getJob(job._id));
  };

  const onDeleteComment = async (commentId, jobId) => {
    await DELETE_COMMENT(commentId);
    socket.emit(SOCKET_EVENT.REFRESH_JOB, { jobId });
    dispatch(getJob(jobId));
  };

  /* ---- Share machinery (reused from FeedItem) ---- */
  const [shareUrl, setShareUrl] = useState("none");
  const [shareUrlLi, setShareUrlLi] = useState("none");
  const fbBtnRef = useRef(null);
  const liBtnRef = useRef(null);
  const dlBtnRef = useRef(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareBtnRef = useRef(null);

  const getShareableUrl = async () => {
    const { _id, shareable, updatedAt } = job;
    if (shareable?.key && shareable.timestamp >= updatedAt) {
      return getSocialUrl(shareable.key);
    }
    const url = await getFacebookShareable(job._id, images, companyLogo, primaryColor);
    dispatch(getJob(_id));
    return url;
  };

  const handleShareFacebook = async () => {
    setShareMenuOpen(false);
    if (shareUrl === "none") {
      try {
        const u = await getShareableUrl();
        setShareUrl(u);
        setTimeout(() => fbBtnRef.current?.click(), 200);
      } catch (err) {
        shareToast.error("Couldn't prepare share image", "Please try again in a moment.");
      }
    } else {
      fbBtnRef.current?.click();
    }
  };

  const handleShareLinkedIn = async () => {
    setShareMenuOpen(false);
    if (shareUrlLi === "none") {
      try {
        const u = await getShareableUrl();
        setShareUrlLi(u);
        setTimeout(() => liBtnRef.current?.click(), 200);
      } catch (err) {
        shareToast.error("Couldn't prepare share image", "Please try again in a moment.");
      }
    } else {
      liBtnRef.current?.click();
    }
  };

  const handleDownload = async () => {
    setShareMenuOpen(false);
    const toastId = shareToast.loading("Preparing download…");
    try {
      let u = shareUrl;
      if (u === "none") {
        u = await getShareableUrl();
        setShareUrl(u);
      }
      shareToast.success("Downloading…", "Check your downloads folder.", { id: toastId });
      setTimeout(() => dlBtnRef.current?.click(), 200);
    } catch (err) {
      shareToast.error("Download failed", "Please try again.", { id: toastId });
    }
  };

  const handleCopyLink = async () => {
    setShareMenuOpen(false);
    try {
      const u = await getShareableUrl();
      await navigator.clipboard.writeText(u);
      shareToast.success("Link copied", "Ready to paste and share.");
    } catch (err) {
      shareToast.error("Couldn't copy link", "Please try again.");
    }
  };

  /* ---- Display values ---- */
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Teammate";
  const roleTag = resolveJobRoleTag(job);
  // Customer name & full address intentionally removed from feed UI per design.
  // We derive just the city from the address so the post still has a sense of place.
  const customerName = undefined;
  const serviceText = metadata?.title || metadata?.description;
  const address = undefined;
  const city = extractCity(metadata?.location);
  const jobTotalNum = jobTotal ?? metadata?.total;
  const durationSec = metadata?.duration || 0;
  const durationStr = (() => {
    if (durationSec <= 0) return "0:00"; // explicit "no time recorded" placeholder
    const h = Math.floor(durationSec / 3600);
    const m = Math.floor((durationSec % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  })();
  // Relative time only for fresh posts; older ones show the actual date —
  // "2 days ago"/"Tuesday"-style labels left no way to see when it happened.
  const postMoment = moment(job.extRefDate || job.updatedAt);
  const timeAgo = moment().diff(postMoment, "hours") < 48
    ? postMoment.fromNow()
    : postMoment.format(postMoment.isSame(moment(), "year") ? "MMM D" : "MMM D, YYYY");

  const showJobTotalCell = cfg.jobTotal !== false && jobTotalNum != null;
  // Always show the time cell when the user hasn't disabled it — render "0:00" when missing.
  const showJobTimeCell = cfg.jobTime !== false;
  const showJobMetaStrip = showJobTotalCell || showJobTimeCell;

  return (
    <Box
      id={elementId}
      sx={{
        background: TOKENS.surface,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow:
          "0 1px 0 rgba(255,255,255,0.6) inset, 0 1px 2px rgba(15,23,42,0.04), 0 18px 36px rgba(15,23,42,0.07)",
        marginBottom: "10px",
        fontFamily: FONT,
        color: TOKENS.ink,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "18px 22px 14px",
          background: "linear-gradient(180deg, #F6F9FF 0%, #FFFFFF 100%)",
          borderBottom: `1px solid ${TOKENS.hairline}`,
        }}
      >
        <Box sx={{ flexShrink: 0, boxShadow: "0 0 0 2px #fff, 0 0 0 3px rgba(37,99,235,0.18)", borderRadius: "50%" }}>
          <SoftProfile src={user?.image} size="md" name={user?.firstName} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontWeight: 600,
              fontSize: "15px",
              letterSpacing: "-0.01em",
              color: TOKENS.ink,
              lineHeight: 1.2,
            }}
          >
            {fullName}
          </Box>
          {/* Role tag always sits on its own line under the name */}
          <Box
            sx={{
              marginTop: "6px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              height: "18px",
              padding: "0 8px",
              borderRadius: "999px",
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.01em",
              textTransform: roleTag ? "capitalize" : "none",
              background: rolePillStyle(roleTag).bg,
              color: rolePillStyle(roleTag).fg,
              "&::before": {
                content: '""',
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                background: "currentColor",
              },
            }}
          >
            {roleTag || "Not Tagged"}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0 }}>
          {/* +99 pts on a single row, smaller */}
          <Box
            sx={{
              fontFamily: FONT,
              fontFeatureSettings: NUM_FEATURES,
              display: "inline-flex",
              alignItems: "baseline",
              gap: "4px",
              background: "linear-gradient(180deg, #16A34A 0%, #15803D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            <Box component="span" sx={{ fontSize: "15px", fontWeight: 600 }}>+{points}</Box>
            <Box component="span" sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.02em" }}>pts</Box>
          </Box>
          {/* Time of post sits under the points */}
          <Box sx={{ fontSize: "11px", color: TOKENS.muted, fontWeight: 500 }}>{timeAgo}</Box>
          <Box
            sx={{
              display: "none", // legacy POINTS caption no longer rendered
              fontSize: "9px",
              fontWeight: 800,
              letterSpacing: "0.16em",
              color: "#15803D",
              marginTop: "3px",
            }}
          >
            POINTS
          </Box>
        </Box>
      </Box>

      {/* PHOTO */}
      {cfg.photo !== false && (
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          {images.length > 0 ? (
            <Box
              sx={{
                position: "relative",
                margin: "12px 16px 0",
                borderRadius: "12px",
                overflow: "hidden",
                "&:hover .photo-arrow": { opacity: images.length > 1 ? 1 : 0 },
              }}
            >
              {/* Scroll-snap track */}
              <Box
                ref={trackRef}
                onScroll={onTrackScroll}
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  scrollSnapType: "x mandatory",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {images.map((img, i) => (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={`slide-${i}`}
                    sx={{
                      flex: "0 0 100%",
                      aspectRatio: "16 / 9",
                      maxHeight: "360px",
                      scrollSnapAlign: "center",
                      backgroundColor: "#E2E8F0",
                      position: "relative",
                      overflow: "hidden",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, transparent 40%, rgba(11,19,32,0.85) 100%)",
                        pointerEvents: "none",
                        zIndex: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Math.abs(i - photoIdx) <= 1 ? img.src : undefined}
                      alt=""
                      loading={i === photoIdx ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={i === photoIdx ? "high" : "low"}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        display: "block",
                      }}
                    />
                    {/* Per-slide overlay (service + customer) */}
                    <Box sx={{ position: "absolute", left: "22px", right: "22px", bottom: "18px", zIndex: 2 }}>
                      {cfg.service !== false && serviceText && (
                        <Box
                          sx={{
                            fontSize: "9px",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            color: "rgba(255,255,255,0.75)",
                            // Sentence case — only the first letter uppercase
                            textTransform: "lowercase",
                            "&::first-letter": { textTransform: "uppercase" },
                            marginBottom: "3px",
                            textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                          }}
                        >
                          Job · {serviceText}
                        </Box>
                      )}
                      {city && (
                        <Box
                          sx={{
                            fontWeight: 700,
                            fontSize: "14px",
                            color: "#fff",
                            letterSpacing: "-0.01em",
                            textShadow: "0 1px 4px rgba(0,0,0,0.4)",
                            lineHeight: 1.2,
                          }}
                        >
                          {city}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Stage tag (current slide's tag) */}
              {images[photoIdx]?.tag && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "16px",
                    left: "22px",
                    zIndex: 3,
                    display: "inline-flex",
                    alignItems: "center",
                    background: "rgba(11,19,32,0.55)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: "999px",
                    padding: "5px 10px",
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    pointerEvents: "none",
                  }}
                >
                  {images[photoIdx].tag}
                </Box>
              )}

              {/* Top-right toolbar: photo count + add */}
              <Box
                sx={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  zIndex: 4,
                  display: "flex",
                  gap: "6px",
                }}
              >
                {images.length > 1 && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      height: "28px",
                      padding: "0 10px",
                      borderRadius: "999px",
                      background: "rgba(11,19,32,0.55)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                      pointerEvents: "none",
                    }}
                  >
                    {photoIdx + 1} / {images.length}
                  </Box>
                )}
                {isOwner && (
                  <Box
                    onClick={() => setShowGallery(true)}
                    role="button"
                    aria-label="Add photo"
                    sx={{
                      // Bigger, higher contrast — visible against any photo or skeleton.
                      // Solid brand-blue chip with a + icon and "Add" label so the action is obvious on mobile.
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      height: "32px",
                      padding: "0 12px 0 10px",
                      borderRadius: "999px",
                      background: TOKENS.primary,
                      border: "1px solid rgba(255,255,255,0.25)",
                      color: "#fff",
                      cursor: "pointer",
                      boxShadow: "0 4px 10px rgba(37,99,235,0.35)",
                      fontSize: "12px",
                      fontWeight: 700,
                      letterSpacing: "0.01em",
                      transition: "transform 120ms ease, box-shadow 120ms ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 14px rgba(37,99,235,0.45)",
                      },
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add
                  </Box>
                )}
              </Box>

              {/* Prev/next arrows (desktop, hover-revealed) */}
              {images.length > 1 && photoIdx > 0 && (
                <Box
                  className="photo-arrow"
                  onClick={() => goToPhoto(-1)}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "12px",
                    transform: "translateY(-50%)",
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "rgba(11,19,32,0.55)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 5,
                    // Always tappable on touch screens (no hover state to reveal
                    // them there) — only hidden-until-hover on sm and up.
                    opacity: { xs: 1, sm: 0 },
                    transition: "opacity 200ms ease",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </Box>
              )}
              {images.length > 1 && photoIdx < images.length - 1 && (
                <Box
                  className="photo-arrow"
                  onClick={() => goToPhoto(1)}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: "12px",
                    transform: "translateY(-50%)",
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "rgba(11,19,32,0.55)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 5,
                    opacity: { xs: 1, sm: 0 },
                    transition: "opacity 200ms ease",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </Box>
              )}

              {/* Dots indicator */}
              {images.length > 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "5px",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  {images.map((_, i) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`dot-${i}`}
                      sx={{
                        width: i === photoIdx ? "18px" : "6px",
                        height: "6px",
                        borderRadius: "999px",
                        background: i === photoIdx ? "#fff" : "rgba(255,255,255,0.4)",
                        transition: "width 200ms ease, background 200ms ease",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ padding: "12px 16px 0" }}>
              {cfg.service !== false && serviceText && (
                <Box
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 500,
                    color: TOKENS.muted,
                    textTransform: "lowercase",
                    "&::first-letter": { textTransform: "uppercase" },
                  }}
                >
                  Job · {serviceText}
                </Box>
              )}
              {city && (
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: "15px",
                    color: TOKENS.ink,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                    marginTop: "2px",
                  }}
                >
                  {city}
                </Box>
              )}
              {isOwner && (
                <Box
                  onClick={() => setShowGallery(true)}
                  role="button"
                  sx={{
                    marginTop: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "11.5px",
                    fontWeight: 500,
                    color: TOKENS.primary,
                    cursor: "pointer",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ flexShrink: 0 }}>
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add a photo
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* JOB META STRIP (Total · Time) */}
      {showJobMetaStrip && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "10px 22px",
            background: TOKENS.surface2,
            borderBottom: `1px solid ${TOKENS.hairline}`,
            fontSize: "12px",
            color: TOKENS.muted,
            flexWrap: "wrap",
          }}
        >
          {showJobTotalCell && (
            <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: "6px" }}>
              <Box sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: 0, color: TOKENS.muted }}>
                Job total
              </Box>
              <Box sx={{ fontWeight: 600, fontSize: "13px", color: TOKENS.ink, fontFeatureSettings: NUM_FEATURES }}>
                {formatCurrency(jobTotalNum)}
              </Box>
            </Box>
          )}
          {showJobTotalCell && showJobTimeCell && (
            <Box sx={{ width: "3px", height: "3px", borderRadius: "50%", background: TOKENS.faint, margin: "0 12px" }} />
          )}
          {showJobTimeCell && (
            <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: "6px" }}>
              <Box sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: 0, color: TOKENS.muted }}>
                Job time
              </Box>
              <Box sx={{ fontWeight: 600, fontSize: "13px", color: TOKENS.ink, fontFeatureSettings: NUM_FEATURES }}>
                {durationStr}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* KPI GRID */}
      {kpiSummary.length > 0 && (
        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              background: TOKENS.surface,
            }}
          >
            {kpiSummary.map((k, i) => (
              <Box
                key={k.kpiId}
                sx={{
                  padding: "14px",
                  borderRight: (i + 1) % 3 === 0 ? "none" : `1px solid ${TOKENS.hairline}`,
                  borderBottom:
                    i < kpiSummary.length - (kpiSummary.length % 3 || 3)
                      ? `1px solid ${TOKENS.hairline}`
                      : "none",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    letterSpacing: 0,
                    color: TOKENS.muted,
                    textTransform: "lowercase",
                    "&::first-letter": { textTransform: "uppercase" },
                    lineHeight: 1.25,
                  }}
                >
                  {k.title}
                </Box>
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: k.isActive ? TOKENS.ink2 : TOKENS.faint,
                    marginTop: "4px",
                    letterSpacing: "-0.01em",
                    fontFeatureSettings: NUM_FEATURES,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    lineHeight: 1.1,
                  }}
                >
                  {formatKpiValue(k)}
                </Box>
              </Box>
            ))}
            {/* Pad the trailing cells of the last row */}
            {kpiSummary.length % 3 !== 0 &&
              Array.from({ length: 3 - (kpiSummary.length % 3) }).map((_, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={`pad-${idx}`} sx={{ background: TOKENS.surface }} />
              ))}
          </Box>
        </Box>
      )}

      {/* CUSTOMER + ADDRESS LINE */}
      {((cfg.customerName !== false && customerName) || (cfg.address !== false && address)) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            padding: "12px 22px",
            fontSize: "13px",
            color: TOKENS.muted,
            background: TOKENS.surface,
          }}
        >
          {cfg.customerName !== false && customerName && (
            <Box sx={{ display: "inline-flex", alignItems: "center", color: TOKENS.ink2, fontWeight: 600 }}>
              {customerName}
            </Box>
          )}
          {cfg.customerName !== false && customerName && cfg.address !== false && address && (
            <Box sx={{ width: "3px", height: "3px", borderRadius: "50%", background: TOKENS.faint, margin: "0 10px" }} />
          )}
          {cfg.address !== false && address && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
              {cfg.locationPin !== false && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.6, flexShrink: 0 }}>
                  <circle cx="12" cy="10" r="3" />
                  <path d="M12 2a8 8 0 0 0-8 8c0 5.5 8 12 8 12s8-6.5 8-12a8 8 0 0 0-8-8z" />
                </svg>
              )}
              <Box>{address}</Box>
              {cfg.locationPin !== false && (
                <Box
                  component="a"
                  href={buildMapsUrl(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "6px",
                    color: TOKENS.primary,
                    fontWeight: 700,
                    textDecoration: "none",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    background: TOKENS.primarySoft,
                    fontSize: "11px",
                    letterSpacing: "0.04em",
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M14 3h7v7" />
                    <path d="M21 3l-9 9" />
                    <path d="M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6" />
                  </svg>
                  Maps
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* FOOTER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "8px 12px",
          borderTop: `1px solid ${TOKENS.hairline}`,
          position: "relative",
        }}
      >
        {/* Clap (uses existing SlowClap) */}
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <SlowClap
            jobId={job._id}
            currentUserId={currentUser?._id}
            reacts={reacts}
            onToggle={onReactToggle}
            showDetails={false}
          />
        </Box>

        {/* Comment toggle */}
        <Box
          component="button"
          onClick={() => setShowComments((v) => !v)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            height: "38px",
            padding: "0 14px",
            borderRadius: "10px",
            background: showComments ? TOKENS.surface2 : "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: FONT,
            fontSize: "13px",
            fontWeight: 600,
            color: TOKENS.ink2,
            "&:hover": { background: TOKENS.surface2 },
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a8 8 0 0 1-12 7l-5 1 1-5a8 8 0 1 1 16-3z" />
          </svg>
          <Box component="span" sx={{ color: TOKENS.muted, fontWeight: 600 }}>
            {(job.comments || []).length}
          </Box>
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* ••• share menu trigger */}
        <Box
          ref={shareBtnRef}
          component="button"
          onClick={() => setShareMenuOpen((v) => !v)}
          sx={{
            width: "38px",
            height: "38px",
            borderRadius: "10px",
            background: shareMenuOpen ? TOKENS.surface2 : "transparent",
            border: "none",
            cursor: "pointer",
            color: shareMenuOpen ? TOKENS.ink : TOKENS.muted,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { background: TOKENS.surface2, color: TOKENS.ink },
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </Box>

        <ShareSheet
          open={shareMenuOpen}
          onClose={() => setShareMenuOpen(false)}
          anchorEl={shareBtnRef.current}
          title="Share this win"
          actions={[
            {
              key: "fb",
              label: "Facebook",
              listLabel: "Share to Facebook",
              onClick: handleShareFacebook,
              bg: "#1877F2",
              icon: <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.99 3.66 9.13 8.44 9.88V14.9H7.9V12h2.54V9.8c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.24.19 2.24.19v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.9h-2.33v6.98C18.34 21.13 22 16.99 22 12z"/></svg>,
            },
            {
              key: "li",
              label: "LinkedIn",
              listLabel: "Share to LinkedIn",
              onClick: handleShareLinkedIn,
              bg: "#0A66C2",
              icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM8.34 17H5.67V9.67h2.67V17zM7 8.34a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zM18.34 17h-2.67v-3.83c0-1 0-2.16-1.32-2.16-1.32 0-1.52 1-1.52 2.1V17h-2.67V9.67h2.56v1h.04a2.81 2.81 0 0 1 2.53-1.39c2.7 0 3.21 1.78 3.21 4.1V17z"/></svg>,
            },
            {
              key: "copy",
              label: "Copy link",
              onClick: handleCopyLink,
              bg: TOKENS.surface2,
              color: TOKENS.ink2,
              icon: (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              ),
            },
            {
              key: "download",
              label: "Download",
              listLabel: "Download as image",
              onClick: handleDownload,
              bg: TOKENS.surface2,
              color: TOKENS.ink2,
              icon: (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7,10 12,15 17,10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              ),
            },
          ]}
        />
      </Box>

      {/* Inline comment thread */}
      {showComments && (
        <Box sx={{ borderTop: `1px solid ${TOKENS.hairline}`, background: TOKENS.surface2, padding: "12px 22px 16px" }}>
          <CommentList
            jobId={job._id}
            comments={job.comments || []}
            currentUser={currentUser}
            onAddComment={onAddComment}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
            onToggleFavorite={onReactToggle}
          />
        </Box>
      )}

      {/* Hidden share buttons (clicked programmatically) */}
      <Box sx={{ display: "none" }}>
        {shareUrl !== "none" && (
          <FacebookShareButton url={shareUrl} ref={fbBtnRef}>
            <span>Facebook</span>
          </FacebookShareButton>
        )}
        {shareUrlLi !== "none" && (
          <LinkedinShareButton url={shareUrlLi} ref={liBtnRef}>
            <span>LinkedIn</span>
          </LinkedinShareButton>
        )}
        {shareUrl !== "none" && (
          <a
            ref={dlBtnRef}
            href={shareUrl}
            download={`game-my-biz-${job._id}.jpg`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
        )}
      </Box>

      {/* Photo upload modal (existing component, self-contained) */}
      {isOwner && (
        <FeedGallery
          jobId={job._id}
          images={job.images}
          open={showGallery}
          exit={() => setShowGallery(false)}
          gallery={gallery}
          setGallery={setGallery}
        />
      )}
    </Box>
  );
}
